import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import type { ChurchService } from "../../../model/launchpad";
import { CONTENT_MAX_WIDTH } from "../../launchpad/LaunchpadLayout";
import { CARD_CHROME } from "../../ui/Card";
import Card from "../../ui/Card";
import WorshipSongsSection from "../../library/WorshipSongsSection";
import { useDiscussionQuestions } from "../../service/DiscussionQuestionsProvider";
import useDocumentTitle from "../../../hook/useDocumentTitle";
import useGoBack from "../../../hook/useGoBack";
import {
  ChevronBackIcon,
  ChevronForwardIcon,
  PlayRectangleIcon,
  QuestionMarkCircleIcon,
  ShareIcon,
} from "../../ui/icons";
import { SAMPLE_LAUNCHPAD } from "../../../sample/launchpad";

// Web port of ServiceDetailView
// (LibraryFeature/View/Screen/ServiceDetailView.swift).
//
// The app puts the description and discussion questions side by side when
// there is room for two 320pt columns; the web content column is narrower than
// that, so they always stack — which is also why the questions preview uses the
// stacked layout's 5-line limit rather than 10.

/** StretchyImageHeader's default height. */
const HEADER_HEIGHT_PX = 300;

/** The navigation title fades in once the header has mostly scrolled away. */
const TITLE_REVEAL_OFFSET = 150;

/** Toolbar height, which the header tucks underneath. */
const TOOLBAR_HEIGHT = "h-14";

export default function ServiceDetail() {
  const { churchID, serviceID } = useParams<{ churchID: string, serviceID: string }>();

  // The sample Launchpad only carries its latest service. Once the API lands
  // this becomes a fetch, which needs both ids — hence both in the URL. The
  // church is matched too, so a service id under the wrong church 404s here
  // the same way it will against the backend.
  const latest = SAMPLE_LAUNCHPAD.latestService;
  const service = latest && latest.churchID === churchID && latest.id === serviceID
    ? latest
    : null;

  return service
    ? <FoundService service={service} />
    : <MissingService />
}

function FoundService(props: { service: ChurchService }) {
  const { t } = useTranslation("serviceCard");
  const { attributes } = props.service;
  useDocumentTitle(attributes.title);

  const scrollY = useWindowScrollY();
  const hasHeader = attributes.thumbnailURL !== null;
  const titleRevealed = scrollY >= TITLE_REVEAL_OFFSET;

  return <ServicePage>
    <Toolbar
      eyebrow={t("featuredSermon")}
      title={attributes.title}
      revealed={titleRevealed}
    />

    {/* With artwork, the header runs up under the floating toolbar, matching
        .ignoresSafeArea(edges: .top). Without it there is nothing to sit
        behind, so content starts below the toolbar. */}
    <div className={`flex flex-col gap-4 ${hasHeader ? "-mt-14" : "pt-2"}`}>
      {attributes.thumbnailURL && (
        <StretchyImageHeader
          imageUrl={attributes.thumbnailURL}
          eyebrow={t("featuredSermon")}
          title={attributes.title}
          scrollY={scrollY}
        />
      )}

      <div className="flex flex-col gap-4 px-4">
        <SermonDetailsCard
          description={attributes.description}
          videoUrl={attributes.videoURL}
        />

        {attributes.discussionQuestions && (
          <DiscussionQuestionsCard
            serviceTitle={attributes.title}
            questions={attributes.discussionQuestions}
          />
        )}
      </div>

      {attributes.songs.length > 0 && (
        <WorshipSongsSection songs={attributes.songs} />
      )}
    </div>
  </ServicePage>
}

function MissingService() {
  const { t } = useTranslation("serviceDetail");
  useDocumentTitle(t("notFoundTitle"));

  return <ServicePage>
    <Toolbar eyebrow={null} title={t("notFoundTitle")} revealed />
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      <p className="text-[17px] font-semibold text-gray-900">{t("notFoundTitle")}</p>
      <p className="max-w-xs text-[15px] text-gray-500">{t("notFoundDescription")}</p>
    </div>
  </ServicePage>
}

// The centred column from LaunchpadLayout, without its top padding or section
// gap: the header has to reach the very top, and the sticky toolbar has to be a
// direct child of the full-height column or it stops sticking once its parent
// scrolls away.
function ServicePage(props: { children: React.ReactNode }) {
  return <div className="min-h-screen bg-page">
    <div className="mx-auto w-full pb-6" style={{ maxWidth: CONTENT_MAX_WIDTH }}>
      {props.children}
    </div>
  </div>
}

function useWindowScrollY(): number {
  const [scrollY, setScrollY] = useState(() => window.scrollY);

  useEffect(() => {
    const update = () => setScrollY(window.scrollY);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  return scrollY;
}

// The toolbar floats over the header. Its background and the inline title stay
// hidden until the header has scrolled away, the way the app toggles
// .toolbarBackground and the principal toolbar item.
function Toolbar(props: { eyebrow: string | null, title: string, revealed: boolean }) {
  const { t } = useTranslation("serviceDetail");
  const goBack = useGoBack();

  // The app's share button has no action yet. Rather than ship a dead control,
  // this uses the Web Share API and hides the button where it is unavailable.
  const canShare = typeof navigator.share === "function";
  const share = () => {
    navigator
      .share({ title: props.title, url: window.location.href })
      // Dismissing the share sheet rejects with AbortError; nothing to do.
      .catch(() => {});
  };

  return <div className="sticky top-0 z-20">
    <div className={`flex ${TOOLBAR_HEIGHT} items-center gap-2 border-b px-4 transition-colors duration-200 ${
      props.revealed
        ? "border-separator bg-surface/80 backdrop-blur-md"
        : "border-transparent"
    }`}>
      <ToolbarButton label={t("back")} onClick={goBack}>
        <ChevronBackIcon className="size-5 shrink-0" />
      </ToolbarButton>

      <div
        aria-hidden={!props.revealed}
        className={`min-w-0 flex-1 text-center transition-opacity duration-200 ${
          props.revealed ? "opacity-100" : "opacity-0"
        }`}
      >
        {props.eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {props.eyebrow}
          </p>
        )}
        <p className="truncate text-[17px] font-semibold leading-tight text-gray-900">
          {props.title}
        </p>
      </div>

      {canShare
        ? <ToolbarButton label={t("share")} onClick={share}>
            <ShareIcon className="size-[18px] shrink-0" />
          </ToolbarButton>
        // Keeps the title centred when there is no share button.
        : <span aria-hidden="true" className="size-9 shrink-0" />}
    </div>
  </div>
}

// Translucent so the buttons read over artwork and over the page alike.
function ToolbarButton(props: { label: string, onClick: () => void, children: React.ReactNode }) {
  return <button
    type="button"
    onClick={props.onClick}
    aria-label={props.label}
    className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface/80 text-gray-900 shadow-sm backdrop-blur-md transition-opacity duration-200 hover:opacity-75"
  >
    {props.children}
  </button>
}

// Web port of StretchyImageHeader (SaltUIFoundation).
//
// Pulling past the top of the page scales the artwork up from its bottom edge,
// like the app's .stretchy(). Only browsers that rubber-band the page (Safari)
// report a negative scroll offset, so elsewhere it simply never stretches.
function StretchyImageHeader(props: {
  imageUrl: string,
  eyebrow: string,
  title: string,
  scrollY: number,
}) {
  const overscroll = Math.max(0, -props.scrollY);
  const scale = (HEADER_HEIGHT_PX + overscroll) / HEADER_HEIGHT_PX;

  return <div className="relative w-full" style={{ height: HEADER_HEIGHT_PX }}>
    {/* The edge fade masks only this layer, not the title below it. */}
    <div
      className="artwork-edge-fade absolute inset-0 origin-bottom overflow-hidden"
      style={{ transform: scale > 1 ? `scale(${scale})` : undefined }}
    >
      <img src={props.imageUrl} alt="" className="size-full object-cover" />
      {/* LinearGradient(.clear -> .black.opacity(0.8), top -> bottom) */}
      <div className="absolute inset-0 bg-linear-to-b from-transparent to-black/80" />
    </div>

    <div className="absolute inset-x-0 bottom-0 p-4 text-white">
      <p className="text-xs font-semibold uppercase tracking-wide opacity-75">
        {props.eyebrow}
      </p>
      <h1 className="text-[22px] font-bold leading-tight">{props.title}</h1>
    </div>
  </div>
}

// SermonDetailsView: the description, a divider only when there is content on
// both sides of it, and a link out to the video. Renders nothing when there is
// neither.
function SermonDetailsCard(props: { description: string | null, videoUrl: string | null }) {
  const { t } = useTranslation("serviceDetail");

  if (!props.description && !props.videoUrl) return null;

  const showsDivider = Boolean(props.description && props.videoUrl);

  return <Card className="flex flex-col gap-2">
    {props.description && (
      <p className="whitespace-pre-line text-[17px] leading-normal text-gray-900">
        {props.description}
      </p>
    )}

    {showsDivider && <hr className="border-separator" />}

    {props.videoUrl && (
      <a
        href={props.videoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex select-none items-center gap-2 self-start text-[17px] font-medium text-accent transition-opacity duration-200 hover:opacity-75 ${
          showsDivider ? "pt-2" : ""
        }`}
      >
        <PlayRectangleIcon className="size-5 shrink-0" />
        {t("watchVideo")}
      </a>
    )}
  </Card>
}

// DiscussionQuestionsCardView: the whole card opens the discussion questions
// sheet. The card itself is the button, since a <div> is not valid inside one.
function DiscussionQuestionsCard(props: { serviceTitle: string, questions: string }) {
  const { t } = useTranslation("serviceCard");
  const presentQuestions = useDiscussionQuestions();

  return <button
    type="button"
    onClick={() => presentQuestions?.({ title: props.serviceTitle, content: props.questions })}
    className={`${CARD_CHROME} flex w-full flex-col gap-2 p-4 text-left transition-opacity duration-200 hover:opacity-75`}
  >
    {/* CardSectionTitle(showChevron: true) */}
    <span className="flex items-center gap-1 text-base font-medium text-gray-500">
      <QuestionMarkCircleIcon />
      {t("discussionQuestions")}
      <ChevronForwardIcon className="size-3.5 shrink-0" />
    </span>

    {/* Text(discussionQuestions).lineLimit(5) — a plain String in SwiftUI is
        not parsed as markdown, so the preview is plain text here too; the
        sheet renders it properly. */}
    <span className="line-clamp-5 whitespace-pre-line text-[17px] leading-normal text-gray-900">
      {props.questions}
    </span>
  </button>
}
